import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Tabs, Tab, Typography, Tooltip, CircularProgress } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useStyles } from "./MachineCenterDetails.styles";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import { Configuration } from "./tabs";
import { TabsEnumMachineCenterDetails } from "./MachineCenterDetailsInterface";
import { FailureDetailsSettings } from "./FailureDetailsSettings/FailureDetailsSettings.view";
import { ModalProductionLines } from "../ProductionLines/components";
import { MachineCenterCreationForm } from "../ProductionLines/forms/MachineCenterCreationForm/MachineCenterCreationForm";
import { IMachineCenterCreationFormValues, IOEEFormProducts } from "../ProductionLines/forms/MachineCenterCreationForm/MachineCenterCreationFormInterfaces";
import { IOeeProductUpsertInput } from "entities/MachineCenters/MachineCentersInterfaces";
import { CORPORATE_BACKOFFICE_PATHS, DOWNTIMEMETHODCAPTURE } from "constants/constants";
import useRolesPermissions from "hooks/useRolesPermissions";
import Layout from "components/Layout";
import DowntimeAppDrawerItems from "pages/Corporate/components/DowntimeAppDrawerItems";
import moment from "moment";
import CheckUserPasswordModal from "components/CheckUserPasswordModal";
import { AreasListContextProvider } from "pages/Corporate/CorporateSetupDashboard/MachineCenterDetails/FailureDetailsSettings/AreasList/context/AreasListContextProvider";
import { FailureDetailsSettingsContextProvider } from "contexts/FailureDetailsSettings/FailureDetailsSettingsProvider";

export const MachineCenterDetails = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(TabsEnumMachineCenterDetails.configuration);
    const [open, setOpen] = useState(false);
    const [openDeleteMachineCenter, setOpenDeleteMachineCenter] = useState(false);
    const [isOngoingEvent, setIsOngoingEvent] = useState(false);
    const [loadingOngoingEvent, setLoadingOngoingEvent] = useState(true);
    const routeParams = useParams();

    const { CORPORATE_PERMISSIONS } = useRolesPermissions();

    const machineCenterId = useMemo(() => Number(routeParams.machineCenterId) || 0, [routeParams.machineCenterId]);

    const { machineCenter, refetch } = MachineCentersEntity.GetMachineCenterDetailsBO(machineCenterId);
    const { updateMachineCenterMutation, apolloMutationError } = MachineCentersEntity.UpdateMachineCenterCorporateBO();
    const { upsertOeeSettings, upsertOeeProducts } = MachineCentersEntity.UpsertMachineCenterOeeSettings();
    const { validateOngoingEvent, validateExistingEvents } = MachineCentersEntity.CheckMachineCenterOngoingEvent();

    const handleDeleteMachineCenter = useCallback(async () => {
        try {
            const noOngoingDowntimeEvent = await validateOngoingEvent(machineCenterId);
            const hasAnEvent = await validateExistingEvents(machineCenterId);
            if (noOngoingDowntimeEvent) {
                const machineCenterValues = {
                    disabled_at: moment.utc().endOf("day").toDate(),
                    ...(!hasAnEvent && { deleted_at: moment.utc().endOf("day").toDate() }),
                };
                const response = await updateMachineCenterMutation({
                    variables: { machineCenterId: machineCenterId, changes: machineCenterValues },
                });
                if (response) {
                    navigate(
                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${machineCenter[0]?.production_line.mill.mill_id}`,
                    );
                } else {
                    throw Error("No response");
                }
            }
        } catch (error) {
            throw Error(JSON.stringify(error));
        }
    }, [
        machineCenterId,
        updateMachineCenterMutation,
        validateOngoingEvent,
        validateExistingEvents,
        machineCenter,
        navigate,
    ]);

    const handleOnClickDelete = () => {
        setOpenDeleteMachineCenter(true);
    };

    const handleOpen = () => setOpen(true);
    const handleClose = useCallback(() => {
        refetch();
        setOpen(false);
    }, [refetch, setOpen]);

    const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
        setActiveTab(newTab);
    };
    const handleBackButton = () =>
        navigate(
            `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${machineCenter[0]?.production_line.mill.mill_id}`,
        );
    const buildOeeProductsUpsertInput = useCallback(
        (products: IOEEFormProducts[], mcId: number): IOeeProductUpsertInput[] => {
            return products.map((p) => {
                const currentValue = p.product_ideal_values?.[0]?.value;
                const valueChanged = p._original_ideal_value !== currentValue;
                const isNew = !p.id;

                const input: IOeeProductUpsertInput = {
                    ...(p.id && { id: p.id }),
                    name: p.name,
                    machine_center_id: mcId,
                    ...(p.deleted_at && { deleted_at: new Date(p.deleted_at).toISOString() }),
                };

                if ((isNew || valueChanged) && currentValue != null && !p.deleted_at) {
                    input.product_ideal_values = { data: [{ value: currentValue }] };
                }

                return input;
            });
        },
        [],
    );

    const handleSubmit = useCallback(
        async (values: IMachineCenterCreationFormValues) => {
            const machineCenterValues = {
                name: values.name,
                short_name: values.short_name,
                ...(values.manufacturer && { manufacturer: values.manufacturer }),
                ...(values.model_number && { model_number: values.model_number }),
                downtime_method_capture_id: values.downtime_method_capture_id,
                ...(values.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC && {
                    plc_address: values.plc_address,
                }),
                ...(values.minimum_downtime && { minimum_downtime: Number(values.minimum_downtime) }),
                ...(values.downtime_trigger_threshold && {
                    downtime_trigger_threshold: Number(values.downtime_trigger_threshold),
                }),
                is_bottleneck: values.is_bottleneck,
                updated_at: new Date(),
            };
            try {
                if (values.is_bottleneck)
                    await MachineCentersEntity.desvinculateMachineCenterBottleneck(
                        machineCenter[0]?.production_line.production_line_id,
                    );
                const response = await updateMachineCenterMutation({
                    variables: { machineCenterId: machineCenterId, changes: machineCenterValues },
                });
                if (response) {
                    await upsertOeeSettings(
                        machineCenterId,
                        values.aspen_plus_oee_enabled,
                        values.oee_input_method_id === "" ? undefined : values.oee_input_method_id,
                        {
                            record_quality_enabled: values.record_quality_enabled,
                            quality_manual_entry: values.quality_manual_entry,
                            quality_calculation_method_id:
                                values.quality_calculation_method_id === ""
                                    ? undefined
                                    : values.quality_calculation_method_id,
                        },
                    );
                    if (values.aspen_plus_oee_enabled && values.oee_products.length > 0) {
                        const productsInput = buildOeeProductsUpsertInput(values.oee_products, machineCenterId);
                        await upsertOeeProducts(productsInput);
                    }
                    handleClose();
                }
            } catch {
                return;
            }
        },
        [updateMachineCenterMutation, handleClose, machineCenter, machineCenterId, upsertOeeSettings, upsertOeeProducts, buildOeeProductsUpsertInput],
    );

    const machineCenterEditionObject: IMachineCenterCreationFormValues = {
        name: machineCenter[0]?.name,
        short_name: machineCenter[0]?.short_name,
        manufacturer: machineCenter[0]?.manufacturer || "",
        model_number: machineCenter[0]?.model_number || "",
        downtime_method_capture_id: machineCenter[0]?.downtime_method_capture.downtime_method_capture_id,
        plc_address: machineCenter[0]?.plc_address || "",
        minimum_downtime: machineCenter[0]?.minimum_downtime,
        downtime_trigger_threshold: machineCenter[0]?.downtime_trigger_threshold,
        is_bottleneck: machineCenter[0]?.is_bottleneck,
        aspen_plus_oee_enabled: machineCenter[0]?.machine_center_modules?.[0]?.is_enabled ?? false,
        oee_input_method_id: machineCenter[0]?.machine_center_oee_config?.input_method_id ?? "",
        record_quality_enabled: machineCenter[0]?.machine_center_oee_config?.record_quality_enabled ?? false,
        quality_manual_entry: machineCenter[0]?.machine_center_oee_config?.quality_manual_entry ?? false,
        quality_calculation_method_id: machineCenter[0]?.machine_center_oee_config?.quality_calculation_method_id ?? "",
        oee_products: (machineCenter[0]?.machine_center_oee_products ?? []).map((p) => ({
            ...p,
            _original_ideal_value: p.product_ideal_values?.[0]?.value,
        })),
        is_duplication_with_selection: false,
        is_duplication_without_selection: false,
    };

    const tabsComponents = {
        [TabsEnumMachineCenterDetails.configuration]: {
            component: <Configuration />,
        },
        [TabsEnumMachineCenterDetails.failureCodes]: {
            component: (
                <FailureDetailsSettingsContextProvider>
                    <AreasListContextProvider>
                        <FailureDetailsSettings machineCenter={machineCenter[0]} />
                    </AreasListContextProvider>
                </FailureDetailsSettingsContextProvider>
            ),
        },
    };

    useEffect(() => {
        const fetchOngoingEvent = async () => {
            setLoadingOngoingEvent(true);
            const noOngoingDowntimeEvent = await validateOngoingEvent(machineCenterId);
            setIsOngoingEvent(!noOngoingDowntimeEvent);
            setLoadingOngoingEvent(false);
        };

        fetchOngoingEvent();
    }, [machineCenterId, validateOngoingEvent]);

    return (
        <Layout appDrawerItems={<DowntimeAppDrawerItems />}>
            <div className={classes.backgroundContainer}>
                <Box className={classes.headerContainer}>
                    <div className={classes.titleContainer}>
                        <IconButton className={classes.backButton} onClick={handleBackButton}>
                            <ArrowBackIosIcon />
                            {t("corporate_backoffice.machine_center_details.back_button")}
                        </IconButton>
                    </div>
                    <Typography className={classes.title}>
                        {machineCenter[0]?.name}{" "}
                        {activeTab === TabsEnumMachineCenterDetails.configuration &&
                            CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters && (
                                <span>
                                    <IconButton className={classes.editButton} onClick={handleOpen}>
                                        <EditIcon />
                                    </IconButton>
                                    {CORPORATE_PERMISSIONS?.corporationPermissions?.canDelete &&
                                        (loadingOngoingEvent ? (
                                            <IconButton disabled={isOngoingEvent} className={classes.editButton}>
                                                <CircularProgress size={24} color="info" />
                                            </IconButton>
                                        ) : isOngoingEvent ? (
                                            <Tooltip
                                                placement="bottom"
                                                PopperProps={{
                                                    className: classes.tooltip,
                                                }}
                                                title={
                                                    <Typography className={classes.tooltipTexts}>
                                                        {t(
                                                            "corporate_backoffice.production_line_view.machine_center_card.remove_tooltip",
                                                        )}
                                                    </Typography>
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        disabled={isOngoingEvent}
                                                        className={classes.editButton}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <IconButton
                                                disabled={isOngoingEvent}
                                                className={classes.editButton}
                                                onClick={handleOnClickDelete}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        ))}
                                </span>
                            )}
                    </Typography>
                </Box>
                <Box className={classes.tabsContainer}>
                    <Tabs value={activeTab} onChange={handleTabChange} className={classes.tabs}>
                        <Tab
                            value={TabsEnumMachineCenterDetails.configuration}
                            label={t("corporate_backoffice.machine_center_details.tabs_labels.configuration")}
                        />
                        <Tab
                            value={TabsEnumMachineCenterDetails.failureCodes}
                            label={t("corporate_backoffice.machine_center_details.tabs_labels.failure_details")}
                        />
                    </Tabs>
                </Box>

                {tabsComponents[activeTab].component}

                <ModalProductionLines
                    open={open}
                    handleClose={handleClose}
                    alignTop
                    title={t(
                        "corporate_backoffice.production_line_view.machine_center_creation_form.modal_edition_title",
                    )}
                >
                    <MachineCenterCreationForm
                        handleSubmit={handleSubmit}
                        handleClose={handleClose}
                        apolloMutationError={apolloMutationError}
                        isEdit
                        machineCenter={machineCenterEditionObject}
                        millId={machineCenter[0]?.production_line.mill.mill_id}
                        machineCenterId={machineCenterId}
                        isMillAspenPlusEnabled={
                            machineCenter[0]?.production_line?.mill?.mill_modules?.[0]?.is_enabled ?? false
                        }
                    />
                </ModalProductionLines>
                <CheckUserPasswordModal
                    open={openDeleteMachineCenter}
                    onConfirmPassword={handleDeleteMachineCenter}
                    onClose={() => setOpenDeleteMachineCenter(false)}
                    title={t(
                        "corporate_backoffice.production_line_view.machine_center_card.delete_machine_center.header",
                    )}
                    question={t(
                        "corporate_backoffice.production_line_view.machine_center_card.delete_machine_center.confirm_modal_question",
                        { name: machineCenter[0]?.name },
                    )}
                    questionConfirmedButtonText={t(
                        "corporate_backoffice.production_line_view.machine_center_card.delete_machine_center.header",
                    )}
                />
            </div>
        </Layout>
    );
};
